<template>
  <transition name="m3d-fade">
    <div class="m3d" v-if="value" @keydown.esc="close" tabindex="-1" ref="root">
      <div class="m3d__bg" @click="close"></div>

      <div class="m3d__panel" :class="{ 'm3d--narrow': narrow }">
        <!-- 顶部标题 -->
        <div class="m3d__head">
          <div class="m3d__t">
            <p class="m3d__eyebrow">3D 结构构建图</p>
            <h3>{{ title }}</h3>
            <p class="m3d__sub" v-if="subtitle">{{ subtitle }}</p>
          </div>
          <button class="m3d__close" @click="close" aria-label="关闭">&#10005;</button>
        </div>

        <!-- 画布 -->
        <div class="m3d__stage" ref="stage">
          <div class="m3d__loading" v-if="loading">正在生成三维模型…</div>
          <div class="m3d__err" v-if="failed">
            当前浏览器不支持 WebGL，无法显示 3D 构建图。
          </div>
          <span
            v-for="(m, i) in markers"
            :key="'mk' + i"
            class="m3d__marker"
            :class="{ on: active === i }"
            :style="{ left: m.x + 'px', top: m.y + 'px', opacity: m.visible ? 1 : 0 }"
            @mouseenter="active = i"
            @click="active = i"
          >{{ i + 1 }}</span>
        </div>

        <!-- 零件清单 -->
        <div class="m3d__list">
          <p class="m3d__listTitle">零件构成</p>
          <ul>
            <li
              v-for="(p, i) in partList"
              :key="'p' + i"
              :class="{ on: active === i }"
              @mouseenter="active = i"
              @mouseleave="active = -1"
            >
              <i>{{ i + 1 }}</i>
              <div>
                <b>{{ p.name }}</b>
                <span>{{ p.note }}</span>
              </div>
            </li>
          </ul>
        </div>

        <!-- 控制条 -->
        <div class="m3d__bar">
          <div class="m3d__ctrl">
            <label>爆炸视图</label>
            <input type="range" min="0" max="100" v-model.number="explode" />
          </div>
          <button :class="{ on: spin }" @click="spin = !spin">自动旋转</button>
          <button @click="reset">重置视角</button>
          <p class="m3d__tip">拖动旋转 · 滚轮缩放 · 右键平移</p>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import * as THREE from 'three'
import { buildModel } from '../../three/models'
import { isMobileUA } from '../../util/device'

// 聚焦某个零件时，其它零件统一压暗到这个颜色（线性空间）
const DIM = new THREE.Color(0x2a3138).convertSRGBToLinear()

export default {
  name: 'Model3D',
  props: {
    value: { type: Boolean, default: false },
    modelKey: { type: String, default: 'press-fitting' },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' }
  },
  data () {
    return {
      loading: true,
      failed: false,
      // 站点在手机上是「桌面布局整体缩放」（viewport 未设 device-width），
      // 媒体查询按不到真实屏宽，所以窄屏形态用 JS 判断后切类名
      narrow: false,
      explode: 0,
      spin: true,
      active: -1,
      partList: [],
      markers: []
    }
  },
  watch: {
    value (v) {
      if (v) {
        this.$nextTick(this.init)
        document.body.style.overflow = 'hidden'
      } else {
        this.dispose()
        document.body.style.overflow = ''
      }
    },
    modelKey () {
      if (this.value) {
        this.dispose()
        this.$nextTick(this.init)
      }
    },
    active (i) { this.highlight(i) }
  },
  beforeDestroy () {
    this.dispose()
    document.body.style.overflow = ''
  },
  methods: {
    close () { this.$emit('input', false) },

    /* ------------------------------------------------------------ 初始化 */
    init () {
      const stage = this.$refs.stage
      if (!stage) return
      this.loading = true
      this.failed = false
      this.explode = 0
      this.active = -1
      this.narrow = isMobileUA() || window.innerWidth < 1000

      let renderer
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
      } catch (e) {
        this.failed = true
        this.loading = false
        return
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      renderer.outputEncoding = THREE.sRGBEncoding
      renderer.toneMapping = THREE.ACESFilmicToneMapping
      renderer.toneMappingExposure = 0.96
      stage.appendChild(renderer.domElement)
      renderer.domElement.className = 'm3d__canvas'
      this.renderer = renderer

      const scene = new THREE.Scene()
      this.scene = scene

      const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 200)
      this.camera = camera

      // 用一张渐变贴图当环境光照，金属件才有正确的高光与反射
      const env = new THREE.CanvasTexture(this.envCanvas())
      env.mapping = THREE.EquirectangularReflectionMapping
      const pmrem = new THREE.PMREMGenerator(renderer)
      scene.environment = pmrem.fromEquirectangular(env).texture
      env.dispose()
      pmrem.dispose()

      scene.add(new THREE.HemisphereLight(0xdfe9ef, 0x18202a, 0.55))
      const key = new THREE.DirectionalLight(0xffffff, 1.25)
      key.position.set(6, 9, 7)
      const fill = new THREE.DirectionalLight(0x9fc4d8, 0.7)
      fill.position.set(-7, 3, -5)
      const rim = new THREE.DirectionalLight(0xffffff, 0.9)
      rim.position.set(0, -4, -8)
      scene.add(key, fill, rim)

      // 装配模型
      const model = buildModel(this.modelKey)
      this.model = model
      const root = new THREE.Group()
      root.rotation.set(model.rotate[0], model.rotate[1], model.rotate[2])
      model.parts.forEach(p => {
        // 各零件独占材质，聚焦某个零件时才不会连带改变共用同一材质的其它零件
        p.traverse(o => { if (o.isMesh) o.material = o.material.clone() })
        root.add(p)
      })
      scene.add(root)
      this.root = root
      scene.add(this.shadowPlane(model.fit))

      this.partList = model.parts.map(p => ({ name: p.userData.name, note: p.userData.note }))
      this.markers = model.parts.map(() => ({ x: -999, y: -999, visible: false }))

      // 相机初始位姿
      this.home = new THREE.Vector3(model.camera[0], model.camera[1], model.camera[2])
      this.target = new THREE.Vector3(0, 0, 0)
      this.orbit = {
        theta: Math.atan2(this.home.x, this.home.z),
        phi: Math.acos(this.home.y / this.home.length()),
        radius: this.home.length(),
        pan: new THREE.Vector3()
      }
      this.orbitHome = Object.assign({}, this.orbit, { pan: new THREE.Vector3() })

      this.bindInput(renderer.domElement)
      this.onResize = this.resize.bind(this)
      window.addEventListener('resize', this.onResize)
      this.resize()
      this.loading = false
      this.tick()
      if (this.$refs.root) this.$refs.root.focus()
    },

    /** 渐变环境贴图：上亮下暗，模拟摄影棚顶光 */
    envCanvas () {
      const c = document.createElement('canvas')
      c.width = 32
      c.height = 128
      const g = c.getContext('2d')
      const grd = g.createLinearGradient(0, 0, 0, 128)
      grd.addColorStop(0, '#ffffff')
      grd.addColorStop(0.42, '#cfdae2')
      grd.addColorStop(0.52, '#5d6d78')
      grd.addColorStop(1, '#11171c')
      g.fillStyle = grd
      g.fillRect(0, 0, 32, 128)
      return c
    },

    /** 地面接触阴影（一张径向渐变贴图，比实时阴影省很多） */
    shadowPlane (fit) {
      const c = document.createElement('canvas')
      c.width = c.height = 256
      const g = c.getContext('2d')
      const grd = g.createRadialGradient(128, 128, 0, 128, 128, 128)
      grd.addColorStop(0, 'rgba(0,0,0,0.45)')
      grd.addColorStop(0.55, 'rgba(0,0,0,0.16)')
      grd.addColorStop(1, 'rgba(0,0,0,0)')
      g.fillStyle = grd
      g.fillRect(0, 0, 256, 256)
      const tex = new THREE.CanvasTexture(c)
      const m = new THREE.Mesh(
        new THREE.PlaneGeometry(fit * 3, fit * 3),
        new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false })
      )
      m.rotation.x = -Math.PI / 2
      m.position.y = -fit * 0.55
      return m
    },

    /* -------------------------------------------------------- 交互（自研） */
    bindInput (el) {
      const o = this.orbit
      let mode = 0
      let lx = 0
      let ly = 0

      const down = e => {
        mode = e.button === 2 || e.shiftKey ? 2 : 1
        lx = e.clientX
        ly = e.clientY
        this.spin = false
        el.setPointerCapture && el.setPointerCapture(e.pointerId)
      }
      const move = e => {
        if (!mode) return
        const dx = e.clientX - lx
        const dy = e.clientY - ly
        lx = e.clientX
        ly = e.clientY
        if (mode === 1) {
          o.theta -= dx * 0.006
          o.phi = Math.max(0.12, Math.min(Math.PI - 0.12, o.phi - dy * 0.006))
        } else {
          const k = o.radius * 0.0016
          o.pan.x -= dx * k * Math.cos(o.theta)
          o.pan.z += dx * k * Math.sin(o.theta)
          o.pan.y += dy * k
        }
      }
      const up = e => {
        mode = 0
        el.releasePointerCapture && e.pointerId != null &&
          el.hasPointerCapture && el.hasPointerCapture(e.pointerId) &&
          el.releasePointerCapture(e.pointerId)
      }
      const wheel = e => {
        e.preventDefault()
        o.radius = Math.max(2.2, Math.min(30, o.radius * (1 + Math.sign(e.deltaY) * 0.12)))
      }

      // 触屏：单指旋转、双指缩放
      let pinch = 0
      const tstart = e => {
        this.spin = false
        if (e.touches.length === 1) {
          mode = 1
          lx = e.touches[0].clientX
          ly = e.touches[0].clientY
        } else if (e.touches.length === 2) {
          mode = 0
          pinch = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY)
        }
      }
      const tmove = e => {
        if (e.touches.length === 1 && mode === 1) {
          move({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY })
        } else if (e.touches.length === 2) {
          const d = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY)
          if (pinch) o.radius = Math.max(2.2, Math.min(30, o.radius * pinch / d))
          pinch = d
        }
        e.preventDefault()
      }

      el.addEventListener('pointerdown', down)
      el.addEventListener('pointermove', move)
      el.addEventListener('pointerup', up)
      el.addEventListener('pointercancel', up)
      el.addEventListener('wheel', wheel, { passive: false })
      el.addEventListener('contextmenu', e => e.preventDefault())
      el.addEventListener('touchstart', tstart, { passive: false })
      el.addEventListener('touchmove', tmove, { passive: false })
      el.addEventListener('touchend', () => { mode = 0; pinch = 0 })
      this.unbind = () => {
        el.removeEventListener('pointerdown', down)
        el.removeEventListener('pointermove', move)
        el.removeEventListener('pointerup', up)
        el.removeEventListener('pointercancel', up)
        el.removeEventListener('wheel', wheel)
      }
    },

    reset () {
      Object.assign(this.orbit, {
        theta: this.orbitHome.theta,
        phi: this.orbitHome.phi,
        radius: this.orbitHome.radius
      })
      this.orbit.pan.set(0, 0, 0)
      this.explode = 0
      this.spin = true
    },

    highlight (idx) {
      if (!this.model) return
      this.model.parts.forEach((p, i) => {
        p.traverse(o => {
          if (!o.isMesh) return
          if (!o.userData._c) o.userData._c = o.material.color.clone()
          const dim = idx >= 0 && i !== idx
          o.material.color.copy(o.userData._c)
          if (dim) o.material.color.lerp(DIM, 0.62)
          if (o.material.emissive) {
            o.material.emissive.setHex(i === idx ? 0x1d3a33 : 0x000000)
          }
        })
      })
    },

    resize () {
      const s = this.$refs.stage
      if (!s || !this.renderer) return
      const w = s.clientWidth
      const h = s.clientHeight
      this.renderer.setSize(w, h, false)
      const aspect = w / Math.max(1, h)
      this.camera.aspect = aspect
      this.camera.updateProjectionMatrix()
      // 画布窄而高时（手机竖屏），横向视野不够，镜头按宽高比后退
      this.aspectK = Math.max(1, 1 / aspect)
    },

    /* ---------------------------------------------------------- 渲染循环 */
    tick () {
      if (!this.renderer) return
      this.raf = requestAnimationFrame(this.tick)
      const o = this.orbit
      if (this.spin) o.theta += 0.0035

      // 爆炸位移（带缓动，拖动滑块时零件平滑散开）
      const t = this.explode / 100
      this.model.parts.forEach(p => {
        const u = p.userData
        p.position.lerp(
          u.home.clone().addScaledVector(u.explode, t), 0.18)
      })

      // 零件散开后整体尺寸变大，镜头同步后退，避免爆炸视图被裁切
      const r = o.radius * (this.aspectK || 1) * (1 + t * 0.55)
      const sp = Math.sin(o.phi)
      this.camera.position.set(
        o.pan.x + r * sp * Math.sin(o.theta),
        o.pan.y + r * Math.cos(o.phi),
        o.pan.z + r * sp * Math.cos(o.theta)
      )
      this.camera.lookAt(o.pan)
      this.renderer.render(this.scene, this.camera)
      this.updateMarkers()
    },

    updateMarkers () {
      const s = this.$refs.stage
      if (!s) return
      const w = s.clientWidth
      const h = s.clientHeight
      const box = new THREE.Box3()
      const v = new THREE.Vector3()
      const show = this.explode > 6
      this.model.parts.forEach((p, i) => {
        const mk = this.markers[i]
        if (!show) { if (mk.visible) { mk.visible = false } return }
        box.setFromObject(p)
        box.getCenter(v)
        v.project(this.camera)
        mk.x = Math.round((v.x * 0.5 + 0.5) * w)
        mk.y = Math.round((-v.y * 0.5 + 0.5) * h)
        mk.visible = v.z < 1 && mk.x > 14 && mk.x < w - 14 && mk.y > 14 && mk.y < h - 14
      })
    },

    /* ---------------------------------------------------------------- 销毁 */
    dispose () {
      if (this.raf) cancelAnimationFrame(this.raf)
      this.raf = null
      if (this.onResize) window.removeEventListener('resize', this.onResize)
      if (this.unbind) this.unbind()
      if (this.scene) {
        this.scene.traverse(o => {
          if (o.geometry) o.geometry.dispose()
          if (o.material) {
            const mats = Array.isArray(o.material) ? o.material : [o.material]
            mats.forEach(m => {
              if (m.map) m.map.dispose()
              m.dispose()
            })
          }
        })
      }
      if (this.renderer) {
        this.renderer.dispose()
        const el = this.renderer.domElement
        if (el && el.parentNode) el.parentNode.removeChild(el)
      }
      this.renderer = this.scene = this.camera = this.model = this.root = null
      this.markers = []
      this.partList = []
    }
  }
}
</script>

<style lang="less">
/* 画布由 three 动态插入，不能用 scoped */
.m3d__canvas {
  display: block;
  width: 100%;
  height: 100%;
  cursor: grab;
  touch-action: none;
}
.m3d__canvas:active { cursor: grabbing; }
</style>

<style scoped lang="less">
@ink: #0d1418;
@line: rgba(255, 255, 255, 0.14);
@accent: #16a37f;

.m3d-fade-enter-active, .m3d-fade-leave-active { transition: opacity 0.25s; }
.m3d-fade-enter, .m3d-fade-leave-to { opacity: 0; }

.m3d {
  position: fixed;
  z-index: 4000;
  top: 0; left: 0; right: 0; bottom: 0;
  outline: none;
}
.m3d__bg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(6, 11, 14, 0.86);
  backdrop-filter: blur(6px);
}
.m3d__panel {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 88%;
  max-width: 1240px;
  height: 84vh;
  background: linear-gradient(160deg, #161f26 0%, @ink 55%, #0a1014 100%);
  border: 1px solid @line;
  border-radius: 6px;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.6);
  display: grid;
  grid-template-columns: 1fr 300px;
  grid-template-rows: auto 1fr auto;
  grid-template-areas: 'head list' 'stage list' 'bar list';
  overflow: hidden;
}

.m3d__head {
  grid-area: head;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 26px 30px 10px;
  h3 { margin: 4px 0 0; font-size: 26px; color: #fff; font-weight: 600; }
}
.m3d__eyebrow {
  margin: 0;
  font-size: 12px;
  letter-spacing: 3px;
  color: @accent;
  text-transform: uppercase;
}
.m3d__sub { margin: 8px 0 0; font-size: 14px; color: rgba(255, 255, 255, 0.5); }
.m3d__close {
  background: none;
  border: 1px solid @line;
  color: rgba(255, 255, 255, 0.7);
  width: 36px; height: 36px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  transition: 0.2s;
  &:hover { background: @accent; border-color: @accent; color: #fff; }
}

.m3d__stage {
  grid-area: stage;
  position: relative;
  min-height: 0;
  overflow: hidden;
}
.m3d__loading, .m3d__err {
  position: absolute;
  top: 50%; left: 0; right: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.45);
  font-size: 14px;
}
.m3d__marker {
  position: absolute;
  width: 24px; height: 24px;
  margin: -12px 0 0 -12px;
  border-radius: 50%;
  background: rgba(13, 20, 24, 0.85);
  border: 1px solid @accent;
  color: #7fe3c6;
  font-size: 12px;
  line-height: 22px;
  text-align: center;
  cursor: pointer;
  transition: opacity 0.25s, background 0.2s, color 0.2s;
  &.on { background: @accent; color: #fff; }
}

.m3d__list {
  grid-area: list;
  border-left: 1px solid @line;
  padding: 26px 0 20px;
  overflow-y: auto;
  ul { list-style: none; margin: 0; padding: 0; }
  li {
    display: flex;
    gap: 12px;
    padding: 12px 24px;
    cursor: default;
    border-left: 2px solid transparent;
    transition: 0.2s;
    i {
      flex: none;
      font-style: normal;
      width: 22px; height: 22px;
      line-height: 20px;
      text-align: center;
      font-size: 12px;
      border-radius: 50%;
      border: 1px solid @line;
      color: rgba(255, 255, 255, 0.55);
    }
    b { display: block; font-size: 14px; color: #fff; font-weight: 500; }
    span {
      display: block;
      margin-top: 5px;
      font-size: 12px;
      line-height: 1.7;
      color: rgba(255, 255, 255, 0.42);
    }
    &.on {
      background: rgba(22, 163, 127, 0.1);
      border-left-color: @accent;
      i { border-color: @accent; color: #7fe3c6; }
    }
  }
}
.m3d__listTitle {
  margin: 0 0 10px;
  padding: 0 24px;
  font-size: 12px;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.35);
}

.m3d__bar {
  grid-area: bar;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 14px 30px 20px;
  border-top: 1px solid @line;
  button {
    background: none;
    border: 1px solid @line;
    color: rgba(255, 255, 255, 0.7);
    padding: 7px 16px;
    font-size: 13px;
    border-radius: 3px;
    cursor: pointer;
    transition: 0.2s;
    &:hover { border-color: @accent; color: #fff; }
    &.on { background: @accent; border-color: @accent; color: #fff; }
  }
}
.m3d__ctrl {
  display: flex;
  align-items: center;
  gap: 12px;
  label { font-size: 13px; color: rgba(255, 255, 255, 0.6); white-space: nowrap; }
  input { width: 190px; accent-color: @accent; cursor: pointer; }
}
.m3d__tip {
  margin: 0 0 0 auto;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
}

/* 窄屏形态：单列排版，并且尺寸全部改用 vw —— 站点在手机上是把桌面布局
   整体缩放显示的，用 px 会被一起缩到看不清，vw 才能对上真实屏幕比例。 */
.m3d__panel.m3d--narrow {
  width: 94%;
  height: 92vh;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr auto 25vh;
  grid-template-areas: 'head' 'stage' 'bar' 'list';

  .m3d__head {
    padding: 3.6vw 4vw 1.6vw;
    h3 { font-size: 4.8vw; }
  }
  .m3d__eyebrow { font-size: 2.5vw; letter-spacing: 0.6vw; }
  .m3d__sub { font-size: 3vw; margin-top: 1.4vw; }
  .m3d__close { width: 8.4vw; height: 8.4vw; font-size: 3.6vw; }

  .m3d__marker { width: 6vw; height: 6vw; margin: -3vw 0 0 -3vw; font-size: 3vw; line-height: 5.6vw; }

  .m3d__list {
    border-left: none;
    border-top: 1px solid @line;
    max-height: none;
    padding: 2.4vw 0 3vw;
    li {
      padding: 2vw 4vw;
      gap: 2.4vw;
      i { width: 5.2vw; height: 5.2vw; line-height: 4.8vw; font-size: 2.8vw; }
      b { font-size: 3.4vw; }
      span { font-size: 2.8vw; margin-top: 0.8vw; line-height: 1.5; }
    }
  }
  .m3d__listTitle { font-size: 2.6vw; padding: 0 4vw; margin-bottom: 1.6vw; }

  .m3d__bar {
    flex-wrap: nowrap;
    gap: 2.2vw;
    padding: 2.6vw 3vw 3.4vw;
    button { font-size: 3vw; padding: 1.8vw 3vw; white-space: nowrap; }
  }
  .m3d__ctrl {
    gap: 1.8vw;
    label { font-size: 3vw; }
    input { width: 22vw; }
  }
  .m3d__tip { display: none; }
  .m3d__loading, .m3d__err { font-size: 3.2vw; }
}
</style>
